import {
  BLOCK_PROP_TYPES,
  BLOCK_TYPES,
  BUILT_IN_BLOCKS,
  BUILT_IN_BLOCK_TYPE_SET,
  CUSTOM_BLOCK_PREFIX,
  FIELD_TYPES,
  LIST_BLOCK_TYPES,
  SECTION_ROUTE_PREFIXES,
  TEXT_BLOCK_TYPES,
  blockContentSchema,
  blockDefSchema,
  blockDefToSnippet,
  blockKey,
  blockMetaFromDef,
  blockPropDefSchema,
  blockPropsSchemaFor,
  blockTypeSchema,
  canonicalize,
  collectionDefSchema,
  customBlockContentSchema,
  customBlockKey,
  customBlockType,
  defaultPropsFor,
  defaultRowFor,
  defineBlock,
  defineCollection,
  defineConfig,
  defineSection,
  fieldDefSchema,
  fieldKey,
  fields,
  isCustomBlockType,
  isTextBlock,
  loopConfigSchema,
  parseBlockContent,
  previewPathFor,
  resolveBlockMeta,
  resolveSectionUrl,
  sectionDefSchema,
  sectionKey,
  sectionPathAllowed,
  templateBlockSchema,
  valueSchemaFor
} from "./chunk-UC2PUIGR.js";

// src/diff.ts
function affectedKey(change) {
  return change.block ? `block:${change.block}` : `${change.collection}.${change.field ?? "*"}`;
}
function diffSchema(prev, next) {
  const changes = [];
  const before = prev?.collections ?? {};
  const after = next.collections;
  for (const [key, collection] of Object.entries(after)) {
    if (!(key in before)) {
      changes.push({
        kind: "collection-added",
        collection: key,
        breaking: false,
        summary: `Collection "${collection.name}" is added.`
      });
      continue;
    }
    changes.push(...diffCollection(key, before[key], collection));
  }
  for (const [key, collection] of Object.entries(before)) {
    if (key in after) continue;
    changes.push({
      kind: "collection-removed",
      collection: key,
      breaking: true,
      summary: `Collection "${collection.name}" is removed. Its entries will be orphaned.`
    });
  }
  changes.push(...diffBlocks(prev?.blocks ?? [], next.blocks ?? []));
  changes.push(...diffSections(prev?.sections ?? [], next.sections ?? []));
  return { changes, breaking: changes.filter((c) => c.breaking), hasChanges: changes.length > 0 };
}
function diffSections(before, after) {
  const changes = [];
  const prevByKey = new Map(before.map((s) => [s.key, s]));
  for (const section of after) {
    const old = prevByKey.get(section.key);
    if (!old) {
      changes.push({ kind: "section-added", section: section.key, breaking: false, summary: `Section "${section.name}" is added.` });
      continue;
    }
    if (canonicalize(old) !== canonicalize(section)) {
      changes.push({ kind: "section-changed", section: section.key, breaking: false, summary: `Section "${section.name}" changes its definition.` });
    }
  }
  const nextKeys = new Set(after.map((s) => s.key));
  for (const section of before) {
    if (nextKeys.has(section.key)) continue;
    changes.push({ kind: "section-removed", section: section.key, breaking: false, summary: `Section "${section.name}" is removed.` });
  }
  return changes;
}
function diffBlocks(before, after) {
  const changes = [];
  const prevByKey = new Map(before.map((b) => [b.key, b]));
  for (const block of after) {
    const old = prevByKey.get(block.key);
    if (!old) {
      changes.push({
        kind: "block-added",
        block: block.key,
        breaking: false,
        summary: `Custom block "${block.name}" is added.`
      });
      continue;
    }
    if (canonicalize(old) !== canonicalize(block)) {
      changes.push({
        kind: "block-changed",
        block: block.key,
        breaking: false,
        summary: `Custom block "${block.name}" changes its definition.`
      });
    }
  }
  const nextKeys = new Set(after.map((b) => b.key));
  for (const block of before) {
    if (nextKeys.has(block.key)) continue;
    changes.push({
      kind: "block-removed",
      block: block.key,
      breaking: true,
      summary: `Custom block "${block.name}" is removed. Blocks using it will render as unknown.`
    });
  }
  return changes;
}
function diffCollection(key, before, after) {
  const changes = [];
  const label = after.name;
  for (const [name, field] of Object.entries(after.fields)) {
    const old = before.fields[name];
    if (!old) {
      changes.push({
        kind: "field-added",
        collection: key,
        field: name,
        to: field.type,
        breaking: field.required,
        summary: field.required ? `Required field "${name}" is added to ${label}. Existing entries have no value for it.` : `Field "${name}" is added to ${label}.`
      });
      continue;
    }
    if (old.type !== field.type) {
      changes.push({
        kind: "field-retyped",
        collection: key,
        field: name,
        from: old.type,
        to: field.type,
        breaking: true,
        summary: `Field "${name}" on ${label} changes type from ${old.type} to ${field.type}.`
      });
      continue;
    }
    if (!old.required && field.required) {
      changes.push({
        kind: "field-required",
        collection: key,
        field: name,
        breaking: true,
        summary: `Field "${name}" on ${label} becomes required. Entries without a value will not validate.`
      });
    }
  }
  for (const name of Object.keys(before.fields)) {
    if (name in after.fields) continue;
    changes.push({
      kind: "field-removed",
      collection: key,
      field: name,
      breaking: true,
      summary: `Field "${name}" is removed from ${label}. Stored values become unreachable.`
    });
  }
  return changes;
}

// src/api.ts
var AI_MODELS = ["claude-sonnet-5-5", "claude-opus-5-5", "claude-haiku-5-5"];
var ASSIST_QUICK_PROMPTS = [
  { key: "outline", label: "Outline this piece" },
  { key: "tighten", label: "Tighten the draft" },
  { key: "titles", label: "Suggest titles" },
  { key: "meta", label: "Write the meta description" },
  { key: "gaps", label: "Find gaps a reader would notice" }
];
var ASSIST_HISTORY_MAX = 20;
var PROPOSABLE_BLOCK_TYPES = ["heading", "text", "quote", "code", "bulletedList", "numberedList", "divider"];
var FORM_FIELD_TYPES = [
  "text",
  "email",
  "textarea",
  "number",
  "select",
  "checkbox",
  "radio",
  "date",
  "hidden"
];
var TOKEN_SCOPES = ["content:read", "preview:read", "schema:write", "forms:write", "content:write"];
var SITE_KINDS = ["production", "staging"];
var DEPLOY_STATUSES = ["started", "succeeded", "failed"];

// src/client.ts
var LoopApiError = class extends Error {
  constructor(status, message, code, references) {
    super(message);
    this.status = status;
    this.code = code;
    this.references = references;
  }
  status;
  code;
  references;
  name = "LoopApiError";
};
function createClient(options = {}) {
  const base = (options.baseUrl ?? "").replace(/\/+$/, "");
  const doFetch = options.fetch ?? globalThis.fetch;
  async function throwForFailedResponse(response) {
    const parsed = await response.json().catch(() => ({ message: `Request failed with status ${response.status}` }));
    const payload = parsed;
    throw new LoopApiError(
      response.status,
      payload.message ?? `Request failed with status ${response.status}`,
      payload.code,
      payload.references
    );
  }
  async function request(path, method, body) {
    const headers = { accept: "application/json", "x-requested-with": "LoopCMS" };
    if (body !== void 0) headers["content-type"] = "application/json";
    if (options.token) headers.authorization = `Bearer ${options.token}`;
    const init = { headers, credentials: "include" };
    if (method) init.method = method;
    if (body !== void 0) init.body = JSON.stringify(body);
    const response = await doFetch(`${base}${path}`, init);
    if (!response.ok) return throwForFailedResponse(response);
    if (response.status === 204) return void 0;
    return await response.json();
  }
  const query = (params) => {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== void 0 && value !== "") search.set(key, value);
    }
    const encoded = search.toString();
    return encoded ? `?${encoded}` : "";
  };
  return {
    health: () => request("/api/health"),
    signIn: (instance, email, password) => request("/api/auth/sign-in", "POST", { instance, email, password }),
    verifyTotp: (code) => request("/api/auth/verify-totp", "POST", { code }),
    signOut: () => request("/api/auth/sign-out", "POST"),
    me: () => request("/api/auth/me"),
    getSchema: () => request("/api/schema"),
    planSchema: (definition) => request("/api/schema/plan", "POST", { definition }),
    applySchema: (definition, acknowledgeBreaking) => request("/api/schema/apply", "POST", { definition, acknowledgeBreaking }),
    listEntries: (q = {}) => request(
      `/api/entries${query({ collection: q.collection, status: q.status, q: q.q, reviewer: q.reviewer })}`
    ),
    createEntry: (collectionKey, title) => request("/api/entries", "POST", { collectionKey, title }),
    getEntry: (id) => request(`/api/entries/${id}`),
    patchEntry: (id, patch) => request(`/api/entries/${id}`, "PATCH", patch),
    deleteEntry: (id) => request(`/api/entries/${id}`, "DELETE"),
    listEntryCounts: (q = {}) => request(`/api/entries/counts${query({ collection: q.collection, q: q.q })}`),
    listVersions: (entryId) => request(`/api/entries/${entryId}/versions`),
    getVersion: (entryId, versionId) => request(`/api/entries/${entryId}/versions/${versionId}`),
    createVersion: (entryId, label) => request(`/api/entries/${entryId}/versions`, "POST", { label }),
    restoreVersion: (entryId, versionId) => request(`/api/entries/${entryId}/versions/${versionId}/restore`, "POST"),
    // Area 02 owns the route; the reviewer picker reads it.
    /** Every instance setting, redacted: secret fields come back as `{ set: boolean }`. */
    getSettings: () => request("/api/settings"),
    patchSetting: (key, patch) => request(`/api/settings/${key}`, "PATCH", patch),
    getStorageInfo: () => request("/api/settings/storage"),
    getStorageUsage: () => request("/api/settings/storage/usage"),
    testStorage: () => request("/api/settings/storage/test", "POST"),
    /** The Updates screen's check; `refresh` bypasses the server's hour-long cache. */
    checkUpdates: (opts = {}) => request(`/api/updates${query({ refresh: opts.refresh ? "1" : void 0 })}`),
    mintPreviewToken: (entryId) => request("/api/preview-token", "POST", { entryId }),
    /** One tiny request on the stored Anthropic key — reports the model and latency, or why it failed. */
    testAiConnection: () => request("/api/settings/ai/test", "POST"),
    getBlocks: (entryId) => request(`/api/entries/${entryId}/blocks`),
    putBlocks: (entryId, blocks) => request(`/api/entries/${entryId}/blocks`, "PUT", { blocks }),
    patchBlock: (blockId, content) => request(`/api/blocks/${blockId}`, "PATCH", { content }),
    createComment: (input) => request("/api/comments", "POST", input),
    listComments: (q = {}) => request(
      `/api/comments${query({ view: q.view, q: q.q, sort: q.sort, cursor: q.cursor, entryId: q.entryId, limit: q.limit?.toString() })}`
    ),
    patchComment: (id, patch) => request(`/api/comments/${id}`, "PATCH", patch),
    deleteComment: (id) => request(`/api/comments/${id}`, "DELETE"),
    bulkResolveComments: (ids) => request("/api/comments/bulk-resolve", "POST", { ids }),
    /** Everything the Home screen shows, in one call. */
    getHome: () => request("/api/home"),
    getSiteMap: () => request("/api/site-map"),
    refreshSiteMap: () => request("/api/site-map/refresh", "POST"),
    listMedia: (q = {}) => request(`/api/media${query({ page: q.page?.toString(), pageSize: q.pageSize?.toString() })}`),
    getMedia: (id) => request(`/api/media/${id}`),
    deleteMedia: (id) => request(`/api/media/${id}`, "DELETE"),
    renameMedia: (id, filename) => request(`/api/media/${id}`, "PATCH", { filename }),
    listWebhooks: () => request("/api/webhooks"),
    createWebhook: (input) => request("/api/webhooks", "POST", input),
    deleteWebhook: (id) => request(`/api/webhooks/${id}`, "DELETE"),
    testWebhook: (id) => request(`/api/webhooks/${id}/test`, "POST"),
    listWebhookDeliveries: (id) => request(`/api/webhooks/${id}/deliveries`),
    listSites: () => request("/api/sites"),
    createSite: (input) => request("/api/sites", "POST", input),
    updateSite: (id, patch) => request(`/api/sites/${id}`, "PATCH", patch),
    deleteSite: (id) => request(`/api/sites/${id}`, "DELETE"),
    rotateSiteSecret: (id) => request(`/api/sites/${id}/secret`, "POST"),
    listSiteDeploys: (id) => request(`/api/sites/${id}/deploys`),
    /** Is this page of the site behind a sign-in? Only addresses on a site the instance knows. */
    getSiteGate: (url) => request(`/api/sites/gate?url=${encodeURIComponent(url)}`),
    /**
     * Multipart upload — the one request this client sends that isn't JSON,
     * so it bypasses `request()` entirely rather than teaching that helper
     * about a second body encoding for a single call site. `FormData` gets
     * no explicit `content-type`: the runtime (browser or undici) sets
     * `multipart/form-data` with the correct boundary itself, which a
     * hand-set header would only get wrong.
     */
    uploadMedia: async (file) => {
      const headers = { accept: "application/json", "x-requested-with": "LoopCMS" };
      if (options.token) headers.authorization = `Bearer ${options.token}`;
      const form = new FormData();
      form.append("file", file, file.name);
      const response = await doFetch(`${base}/api/media`, {
        method: "POST",
        headers,
        body: form,
        credentials: "include"
      });
      if (!response.ok) return throwForFailedResponse(response);
      return await response.json();
    },
    listBlockDefs: () => request("/api/block-defs"),
    setBlockDefStatus: (key, status) => request(`/api/block-defs/${encodeURIComponent(key)}`, "PATCH", { status }),
    listForms: () => request("/api/forms"),
    createForm: (name) => request("/api/forms", "POST", { name }),
    getForm: (id) => request(`/api/forms/${id}`),
    patchForm: (id, patch) => request(`/api/forms/${id}`, "PATCH", patch),
    deleteForm: (id) => request(`/api/forms/${id}`, "DELETE"),
    listSubmissions: (formId, cursor) => request(`/api/forms/${formId}/submissions${query({ cursor })}`),
    /** A plain link — the browser downloads it with the session cookie, so no fetch here. */
    submissionsCsvUrl: (formId) => `${base}/api/forms/${formId}/submissions.csv`,
    getCaptchaStatus: () => request("/api/forms/captcha"),
    testTurnstile: () => request("/api/settings/turnstile/test", "POST"),
    // --- Accounts and security -------------------------------------------
    forgotPassword: (email) => request("/api/auth/forgot", "POST", { email }),
    resetPassword: (token, password) => request("/api/auth/reset", "POST", { token, password }),
    changePassword: (currentPassword, newPassword) => request("/api/auth/change-password", "POST", { currentPassword, newPassword }),
    enrolTotp: () => request("/api/auth/totp/enrol", "POST"),
    confirmTotp: (code) => request("/api/auth/totp/confirm", "POST", { code }),
    disableTotp: (password) => request("/api/auth/totp/disable", "POST", { password }),
    // Passkeys. Options and responses are WebAuthn JSON, passed straight to and from @simplewebauthn/browser.
    /** `secondStep` ties the options to the half-signed-in person (after their password); otherwise any passkey may answer. */
    passkeySignInOptions: (secondStep = false) => request("/api/auth/passkey/options", "POST", secondStep ? { secondStep: true } : {}),
    passkeySignIn: (response) => request("/api/auth/passkey/verify", "POST", { response }),
    passkeyRegisterOptions: () => request("/api/auth/passkeys/register/options", "POST"),
    passkeyRegister: (response, name) => request("/api/auth/passkeys/register/verify", "POST", { response, name }),
    listPasskeys: () => request("/api/auth/passkeys"),
    renamePasskey: (id, name) => request(`/api/auth/passkeys/${id}`, "PATCH", { name }),
    deletePasskey: (id) => request(`/api/auth/passkeys/${id}`, "DELETE"),
    regenerateRecoveryCodes: () => request("/api/auth/recovery-codes", "POST"),
    resetSignIn: (userId) => request(`/api/users/${userId}/reset-sign-in`, "POST"),
    listSessions: () => request("/api/auth/sessions"),
    revokeSession: (id) => request(`/api/auth/sessions/${id}`, "DELETE"),
    revokeOtherSessions: () => request("/api/auth/sessions", "DELETE"),
    getAccount: () => request("/api/auth/account"),
    patchAccount: (patch) => request("/api/auth/account", "PATCH", patch),
    listUsers: () => request("/api/users"),
    patchUser: (id, patch) => request(`/api/users/${id}`, "PATCH", patch),
    removeUser: (id) => request(`/api/users/${id}`, "DELETE"),
    transferOwnership: (userId) => request("/api/users/transfer-ownership", "POST", { userId }),
    listInvites: () => request("/api/invites"),
    createInvite: (email, role) => request("/api/invites", "POST", { email, role }),
    resendInvite: (id) => request(`/api/invites/${id}/resend`, "POST"),
    revokeInvite: (id) => request(`/api/invites/${id}`, "DELETE"),
    getInvite: (token) => request(`/api/invites/accept${query({ token })}`),
    acceptInvite: (token, name, password) => request("/api/invites/accept", "POST", { token, name, password }),
    listTokens: () => request("/api/tokens"),
    createToken: (input) => request("/api/tokens", "POST", input),
    revokeToken: (id) => request(`/api/tokens/${id}`, "DELETE"),
    listAudit: (q = {}) => request(`/api/audit${query({ kind: q.kind, q: q.q, cursor: q.cursor })}`)
  };
}

// src/image.ts
var IMAGE_WIDTHS = [160, 320, 480, 640, 800, 1024, 1280, 1600, 1920, 2560];
var RASTER = /* @__PURE__ */ new Set(["image/jpeg", "image/png", "image/webp", "image/avif"]);
var DEFAULT_SRC_WIDTH = 1024;
var UNKNOWN_WIDTH_CAP = 1920;
function imageUrl(ref, opts) {
  const params = [];
  if (opts.width) params.push(`w=${Math.round(opts.width)}`);
  if (opts.format) params.push(`fm=${opts.format}`);
  if (opts.quality && opts.quality !== "medium") params.push(`q=${opts.quality}`);
  if (params.length === 0) return ref.url;
  const [base, hash] = ref.url.split("#", 2);
  const joined = `${base}${base.includes("?") ? "&" : "?"}${params.join("&")}`;
  return hash === void 0 ? joined : `${joined}#${hash}`;
}
function responsiveImage(ref, opts = {}) {
  const sizes = opts.sizes ?? "100vw";
  const base = { width: ref.width, height: ref.height, alt: ref.alt ?? "", sizes };
  if (!ref.id || !ref.mime || !RASTER.has(ref.mime)) return { ...base, src: ref.url, srcset: "", sources: [] };
  const widths = opts.widths ?? defaultWidths(ref.width);
  const set = (format) => widths.map((w) => `${imageUrl(ref, { width: w === ref.width && !format ? void 0 : w, format, quality: opts.quality })} ${w}w`).join(", ");
  const srcWidth = [...widths].reverse().find((w) => w <= DEFAULT_SRC_WIDTH) ?? widths[0];
  return {
    ...base,
    src: srcWidth === void 0 || srcWidth === ref.width ? imageUrl(ref, { quality: opts.quality }) : imageUrl(ref, { width: srcWidth, quality: opts.quality }),
    srcset: set(),
    sources: [
      { type: "image/avif", srcset: set("avif") },
      { type: "image/webp", srcset: set("webp") }
    ]
  };
}
function defaultWidths(width) {
  if (!width) return IMAGE_WIDTHS.filter((w) => w <= UNKNOWN_WIDTH_CAP);
  return [...IMAGE_WIDTHS.filter((w) => w < width), width];
}

// src/render.ts
function escapeHtml(value) {
  const str = typeof value === "string" ? value : String(value ?? "");
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
function textOf(content) {
  return escapeHtml(content.text);
}
var LIST_TAG = { bulletedList: "ul", numberedList: "ol" };
var CUSTOM_PREFIX = "custom:";
function imageTag(content) {
  const raw = content.url ?? content.src ?? "";
  const media = typeof raw === "object" && raw !== null ? raw : null;
  const alt = escapeHtml(content.alt);
  let size = "";
  if (media && typeof media.width === "number" && typeof media.height === "number") {
    size = ` width="${media.width}" height="${media.height}"`;
  }
  if (media && typeof media.url === "string" && media.id && media.mime) {
    const image = responsiveImage({ id: media.id, url: media.url, width: media.width ?? null, height: media.height ?? null, alt: null, mime: media.mime });
    if (image.sources.length > 0) {
      const sizes = escapeHtml(image.sizes);
      const sources = image.sources.map((s) => `<source type="${s.type}" srcset="${escapeHtml(s.srcset)}" sizes="${sizes}">`).join("");
      return `<figure><picture>${sources}<img src="${escapeHtml(image.src)}" srcset="${escapeHtml(image.srcset)}" sizes="${sizes}" alt="${alt}"${size} loading="lazy" decoding="async"></picture></figure>`;
    }
  }
  const src = escapeHtml(media ? media.url ?? "" : raw);
  return `<figure><img src="${src}" alt="${alt}"${size}></figure>`;
}
function renderList(all, blocks) {
  const out = [];
  let i = 0;
  while (i < blocks.length) {
    const block = blocks[i];
    if (!block) break;
    switch (block.type) {
      case "heading":
        out.push(`<h2>${textOf(block.content)}</h2>`);
        i += 1;
        break;
      case "text":
        out.push(`<p>${textOf(block.content)}</p>`);
        i += 1;
        break;
      case "quote": {
        const attribution = typeof block.content.attribution === "string" ? block.content.attribution.trim() : "";
        out.push(
          `<blockquote><p>${textOf(block.content)}</p>${attribution ? `<cite>${escapeHtml(attribution)}</cite>` : ""}</blockquote>`
        );
        i += 1;
        break;
      }
      case "code":
        out.push(`<pre><code>${textOf(block.content)}</code></pre>`);
        i += 1;
        break;
      case "divider":
        out.push("<hr>");
        i += 1;
        break;
      case "image":
        out.push(imageTag(block.content));
        i += 1;
        break;
      case "bulletedList":
      case "numberedList": {
        const tag = LIST_TAG[block.type];
        const items = [];
        while (i < blocks.length) {
          const next = blocks[i];
          if (!next || next.type !== block.type) break;
          items.push(`<li>${textOf(next.content)}</li>`);
          i += 1;
        }
        out.push(`<${tag}>${items.join("")}</${tag}>`);
        break;
      }
      case "twoColumns": {
        const children = block.id ? all.filter((b) => b.parentBlockId === block.id) : [];
        const column = (index) => `<div class="loop-column">${renderList(all, children.filter((b) => b.columnIndex === index))}</div>`;
        out.push(`<div class="loop-columns">${column(0)}${column(1)}</div>`);
        i += 1;
        break;
      }
      case "customEmbed":
        out.push(typeof block.content.markup === "string" ? block.content.markup : "");
        i += 1;
        break;
      default:
        i += 1;
        break;
    }
  }
  return out.join("");
}
function blocksToHtml(blocks) {
  return renderList(
    blocks,
    blocks.filter((block) => !block.parentBlockId)
  );
}
function renderBlocks(blocks) {
  const segments = [];
  let run = [];
  const flush = () => {
    if (run.length === 0) return;
    const html = renderList(blocks, run);
    if (html) segments.push({ kind: "html", html });
    run = [];
  };
  for (const block of blocks) {
    if (block.parentBlockId) continue;
    if (block.type.startsWith(CUSTOM_PREFIX)) {
      flush();
      segments.push({ kind: "custom", id: block.id ?? null, type: block.type.slice(CUSTOM_PREFIX.length), props: block.content });
    } else {
      run.push(block);
    }
  }
  flush();
  return segments;
}

// src/permissions.ts
var PERMISSIONS = [
  "content.view",
  "content.create",
  "content.edit",
  "content.requestReview",
  "content.approve",
  "content.publish",
  "content.unpublish",
  "content.delete",
  "media.manage",
  "people.manage",
  "tokens.manage",
  "settings.manage",
  "audit.view",
  "assistant.use"
];
var ROLES = ["owner", "admin", "editor", "reviewer", "viewer"];
var ADMINS = ["owner", "admin"];
var WRITERS = ["owner", "admin", "editor"];
var PERMISSION_MATRIX = {
  "content.view": ROLES,
  "content.create": WRITERS,
  "content.edit": WRITERS,
  "content.requestReview": ["owner", "admin", "editor", "reviewer"],
  "content.approve": ["owner", "admin", "reviewer"],
  "content.publish": WRITERS,
  "content.unpublish": ADMINS,
  "content.delete": ADMINS,
  "media.manage": WRITERS,
  "people.manage": ADMINS,
  "tokens.manage": ADMINS,
  "settings.manage": ADMINS,
  "audit.view": ADMINS,
  "assistant.use": ["owner", "admin", "editor", "reviewer"]
};
var PERMISSION_LABELS = {
  "content.view": "View content",
  "content.create": "Create content",
  "content.edit": "Edit content",
  "content.requestReview": "Request review",
  "content.approve": "Approve or request changes",
  "content.publish": "Publish",
  "content.unpublish": "Unpublish",
  "content.delete": "Delete content",
  "media.manage": "Manage media",
  "people.manage": "Manage people",
  "tokens.manage": "Manage API tokens",
  "settings.manage": "Manage settings",
  "audit.view": "View the audit log",
  "assistant.use": "Use the assistant"
};
function can(role, permission) {
  return PERMISSION_MATRIX[permission].includes(role);
}

// src/forms-client.ts
var FormSubmitError = class extends Error {
  constructor(status, message, code, errors) {
    super(message);
    this.status = status;
    this.code = code;
    this.errors = errors;
  }
  status;
  code;
  errors;
  name = "FormSubmitError";
};
function createFormsClient(options) {
  const base = options.url.replace(/\/+$/, "");
  const doFetch = options.fetch ?? globalThis.fetch;
  async function failed(response) {
    const parsed = await response.json().catch(() => ({}));
    throw new FormSubmitError(
      response.status,
      parsed.message ?? `Request failed with status ${response.status}`,
      parsed.code,
      parsed.errors
    );
  }
  return {
    async get(key) {
      const response = await doFetch(`${base}/forms/v1/${encodeURIComponent(key)}`, {
        headers: { accept: "application/json" }
      });
      if (!response.ok) return failed(response);
      return await response.json();
    },
    async submit(key, data, opts = {}) {
      const body = { ...data, _hp: "" };
      if (opts.turnstileToken !== void 0) body.turnstileToken = opts.turnstileToken;
      const response = await doFetch(`${base}/forms/v1/${encodeURIComponent(key)}`, {
        method: "POST",
        headers: { accept: "application/json", "content-type": "application/json" },
        body: JSON.stringify(body)
      });
      if (!response.ok) return failed(response);
      return await response.json();
    }
  };
}

// src/delivery.ts
var FILTER_OPS = ["eq", "ne", "in", "contains", "gt", "gte", "lt", "lte", "exists"];
var SORTABLE_COLUMNS = ["publishedAt", "updatedAt", "title", "slug"];
var WEBHOOK_EVENTS = ["entry.published", "entry.unpublished", "entry.scheduled_published"];

// src/loop.ts
var FORM_REFUSALS = /* @__PURE__ */ new Set(["invalid_input", "captcha_required", "captcha_failed", "captcha_not_configured", "rate_limited"]);
function buildDeliveryQuery(opts) {
  const pairs = [];
  if (opts.limit !== void 0) pairs.push(["limit", String(opts.limit)]);
  if (opts.cursor) pairs.push(["cursor", opts.cursor]);
  if (opts.sort) pairs.push(["sort", opts.sort]);
  if (opts.order) pairs.push(["order", opts.order]);
  if (opts.fields && opts.fields.length > 0) pairs.push(["fields", opts.fields.join(",")]);
  if (opts.render) pairs.push(["render", opts.render]);
  const resolve = /* @__PURE__ */ new Set(["media", ...opts.resolve ?? []]);
  pairs.push(["resolve", [...resolve].sort().join(",")]);
  for (const [field, value] of Object.entries(opts.filter ?? {})) {
    if (value === void 0) continue;
    if (typeof value === "object" && value !== null) {
      for (const [op, operand] of Object.entries(value)) {
        if (operand === void 0) continue;
        const encoded = Array.isArray(operand) ? operand.map(String).join(",") : String(operand);
        pairs.push([`filter[${field}][${op}]`, encoded]);
      }
    } else {
      pairs.push([`filter[${field}]`, String(value)]);
    }
  }
  pairs.sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0);
  const search = new URLSearchParams();
  for (const [key, value] of pairs) search.append(key, value);
  return `?${search.toString()}`;
}
function createLoop(options) {
  const base = options.url.replace(/\/+$/, "");
  const doFetch = options.fetch ?? globalThis.fetch;
  async function failed(response) {
    const parsed = await response.json().catch(() => ({}));
    throw new LoopApiError(response.status, parsed.message ?? `Request failed with status ${response.status}`, parsed.code);
  }
  async function request(path, token, init) {
    const headers = { accept: "application/json" };
    if (token) headers.authorization = `Bearer ${token}`;
    if (init?.body !== void 0) headers["content-type"] = "application/json";
    const response = await doFetch(`${base}${path}`, {
      ...init?.method ? { method: init.method } : {},
      headers,
      ...init?.body !== void 0 ? { body: JSON.stringify(init.body) } : {}
    });
    if (!response.ok) return failed(response);
    return await response.json();
  }
  const orNull = async (promise) => {
    try {
      return await promise;
    } catch (error) {
      if (error instanceof LoopApiError && error.status === 404) return null;
      throw error;
    }
  };
  const collectionApi = (prefix, token, key) => ({
    get: (slug, opts = {}) => orNull(request(`${prefix}/${encodeURIComponent(key)}/${encodeURIComponent(slug)}${buildDeliveryQuery(opts)}`, token)),
    list: (opts = {}) => request(`${prefix}/${encodeURIComponent(key)}${buildDeliveryQuery(opts)}`, token),
    all: async (opts = {}) => {
      const items = [];
      let cursor = null;
      do {
        const page = await request(
          `${prefix}/${encodeURIComponent(key)}${buildDeliveryQuery({ ...opts, cursor })}`,
          token
        );
        items.push(...page.items);
        cursor = page.nextCursor;
      } while (cursor);
      return items;
    }
  });
  const singletonApi = (prefix, token, key) => ({
    get: (opts = {}) => orNull(request(`${prefix}/${encodeURIComponent(key)}${buildDeliveryQuery(opts)}`, token))
  });
  const forms = {
    get: (key) => orNull(request(`/forms/v1/${encodeURIComponent(key)}`, null)),
    submit: async (key, data, opts = {}) => {
      const body = { ...data, _hp: "" };
      if (opts.turnstileToken) body["cf-turnstile-response"] = opts.turnstileToken;
      const response = await doFetch(`${base}/forms/v1/${encodeURIComponent(key)}`, {
        method: "POST",
        headers: { accept: "application/json", "content-type": "application/json" },
        body: JSON.stringify(body)
      });
      const parsed = await response.json().catch(() => null);
      if (parsed && typeof parsed === "object" && "ok" in parsed) return parsed;
      if (parsed && typeof parsed === "object" && typeof parsed["code"] === "string" && FORM_REFUSALS.has(parsed["code"])) {
        const refusal = {
          ok: false,
          code: parsed["code"],
          message: typeof parsed["message"] === "string" ? parsed["message"] : response.statusText
        };
        if (parsed["errors"] && typeof parsed["errors"] === "object") refusal.errors = parsed["errors"];
        return refusal;
      }
      return failed(response);
    }
  };
  const build = (prefix, token) => {
    const api = {};
    for (const [key, def] of Object.entries(options.config.collections)) {
      api[key] = def.singleton ? singletonApi(prefix, token, key) : collectionApi(prefix, token, key);
    }
    api.forms = forms;
    api.preview = (previewToken) => build("/preview/v1", previewToken);
    return api;
  };
  if (typeof options.previewToken === "string" && options.previewToken !== "") {
    return build("/preview/v1", options.previewToken);
  }
  return build("/content/v1", options.token);
}

// src/sections-client.ts
var NOT_IN_LOOPCMS = "This page must be opened from LoopCMS.";
var NULL_BODY_STATUSES = /* @__PURE__ */ new Set([101, 204, 205, 304]);
var FORWARDED_HEADERS = ["content-type", "accept"];
var nextId = 0;
function headerRecord(headers) {
  const out = {};
  if (!headers) return out;
  const normalised = new Headers(headers);
  normalised.forEach((value, name) => {
    if (FORWARDED_HEADERS.includes(name)) out[name] = value;
  });
  return out;
}
function pathOf(input) {
  if (typeof input === "string") {
    if (input.startsWith("/")) return input;
    const url2 = new URL(input);
    return `${url2.pathname}${url2.search}`;
  }
  const url = input instanceof URL ? input : new URL(input.url);
  return `${url.pathname}${url.search}`;
}
function serialiseBody(body) {
  if (body === null || body === void 0) return void 0;
  if (typeof body === "string") return body;
  if (body instanceof FormData) {
    const formData = [];
    body.forEach((value, name) => formData.push([name, value]));
    return { formData };
  }
  throw new Error("A section request body must be a string or FormData.");
}
function connectSection(options) {
  const win = options.win ?? globalThis.window;
  if (!win) return Promise.reject(new Error(NOT_IN_LOOPCMS));
  if (win.parent === win) return Promise.reject(new Error(NOT_IN_LOOPCMS));
  const cms = new URL(options.cms).origin;
  const timeoutMs = options.timeoutMs ?? 1e4;
  const readyIntervalMs = options.readyIntervalMs ?? 250;
  const pending = /* @__PURE__ */ new Map();
  const contextListeners = /* @__PURE__ */ new Set();
  let host = null;
  return new Promise((resolve, reject) => {
    const post = (message) => win.parent.postMessage(message, cms);
    const bridgedFetch = (input, init) => {
      const id = `s${++nextId}`;
      const message = {
        type: "loop:request",
        id,
        path: pathOf(input),
        method: (init?.method ?? "GET").toUpperCase()
      };
      const headers = headerRecord(init?.headers);
      if (Object.keys(headers).length > 0) message.headers = headers;
      const body = serialiseBody(init?.body);
      if (body !== void 0) message.body = body;
      return new Promise((resolveResponse) => {
        pending.set(id, (reply) => {
          resolveResponse(
            new Response(NULL_BODY_STATUSES.has(reply.status) ? null : reply.body, { status: reply.status, headers: reply.headers })
          );
        });
        post(message);
      });
    };
    const onMessage = (event) => {
      if (event.origin !== cms) return;
      const data = event.data;
      if (!data || typeof data !== "object") return;
      if (data.type === "loop:context") {
        const { context } = data;
        if (host) {
          host.context = context;
          for (const listener of [...contextListeners]) listener(context);
          return;
        }
        clearInterval(ready);
        clearTimeout(timer);
        host = {
          context,
          fetch: bridgedFetch,
          client: createClient({ baseUrl: "", fetch: bridgedFetch }),
          onContext: (listener) => {
            contextListeners.add(listener);
            return () => void contextListeners.delete(listener);
          }
        };
        resolve(host);
        return;
      }
      if (data.type === "loop:response") {
        const reply = data;
        const settle = pending.get(reply.id);
        if (!settle) return;
        pending.delete(reply.id);
        settle(reply);
      }
    };
    win.addEventListener("message", onMessage);
    post({ type: "loop:ready" });
    const ready = setInterval(() => post({ type: "loop:ready" }), readyIntervalMs);
    const timer = setTimeout(() => {
      clearInterval(ready);
      win.removeEventListener("message", onMessage);
      reject(new Error(NOT_IN_LOOPCMS));
    }, timeoutMs);
  });
}
export {
  AI_MODELS,
  ASSIST_HISTORY_MAX,
  ASSIST_QUICK_PROMPTS,
  BLOCK_PROP_TYPES,
  BLOCK_TYPES,
  BUILT_IN_BLOCKS,
  BUILT_IN_BLOCK_TYPE_SET,
  CUSTOM_BLOCK_PREFIX,
  DEPLOY_STATUSES,
  FIELD_TYPES,
  FILTER_OPS,
  FORM_FIELD_TYPES,
  FormSubmitError,
  IMAGE_WIDTHS,
  LIST_BLOCK_TYPES,
  LoopApiError,
  NOT_IN_LOOPCMS,
  PERMISSIONS,
  PERMISSION_LABELS,
  PERMISSION_MATRIX,
  PROPOSABLE_BLOCK_TYPES,
  ROLES,
  SECTION_ROUTE_PREFIXES,
  SITE_KINDS,
  SORTABLE_COLUMNS,
  TEXT_BLOCK_TYPES,
  TOKEN_SCOPES,
  WEBHOOK_EVENTS,
  affectedKey,
  blockContentSchema,
  blockDefSchema,
  blockDefToSnippet,
  blockKey,
  blockMetaFromDef,
  blockPropDefSchema,
  blockPropsSchemaFor,
  blockTypeSchema,
  blocksToHtml,
  buildDeliveryQuery,
  can,
  canonicalize,
  collectionDefSchema,
  connectSection,
  createClient,
  createFormsClient,
  createLoop,
  customBlockContentSchema,
  customBlockKey,
  customBlockType,
  defaultPropsFor,
  defaultRowFor,
  defineBlock,
  defineCollection,
  defineConfig,
  defineSection,
  diffSchema,
  fieldDefSchema,
  fieldKey,
  fields,
  imageUrl,
  isCustomBlockType,
  isTextBlock,
  loopConfigSchema,
  parseBlockContent,
  previewPathFor,
  renderBlocks,
  resolveBlockMeta,
  resolveSectionUrl,
  responsiveImage,
  sectionDefSchema,
  sectionKey,
  sectionPathAllowed,
  templateBlockSchema,
  valueSchemaFor
};
